import axios, { AxiosInstance } from 'axios';
import {
  GuideType,
  IProduct,
  IStyleGuide,
  IUser,
  IUserData,
  IUserSubscriptionInfo,
  OrderPaymentsData,
  PaymentMethod,
  SubscriptionStatus,
  TArticleLikePayload,
  TGetLikeDislikesResponse,
  TSubscriptionDiscount,
  TUserPhoto,
  TUserSubscriptionsInfo,
} from 'common-types';
import { TBookmarkPayload, TBookmarksResponse } from 'common-types/bookmarks';
import { useLocalStorageStore } from 'store';
import { APP_TYPE } from 'utils/constants';

import { SERVER_URL, SUBSTITUTION_DOMAIN } from './config';

interface IHttpClient {
  get<Response>(url: string, config?: any): Promise<{ data: Response }>;

  put<Request, Response>(url: string, request: Request, config?: any): Promise<{ data: Response }>;

  post<Request, Response>(url: string, request: Request, config?: any): Promise<{ data: Response }>;

  delete<Response>(url: string, config?: any): Promise<{ data: Response }>;
}

export interface OrderProduct {
  name: 'trial' | 'style' | 'body' | 'hair';
  payment_provider?: PaymentMethod;
}

export interface OrderResponse {
  id: string;
  email?: string;
  userName?: string;
  status?: SubscriptionStatus;
  products: OrderProduct[];
  retest?: boolean;
  attempts?: number;
  schema?: Record<string, any>;
  subscription?: {
    active: true;
  };
}

export interface UserGeoDataResponse {
  country: string;
  currency: string;
  ip: string;
}

interface IApi {
  /**
   * @description service method for checking payment status for common and additional style guides
   * by searching inside products array
   */

  signIn: (payload: {
    email: string;
    password: string;
  }) => Promise<{ token: string; userServiceRefreshToken: string; userServiceToken: string; userId: string }>;

  resetPassword: (email: string) => Promise<void>;

  // renewSubscription: (userId: string) => Promise<void>;

  /**
   * @description sets jwt token it into global httpClient header
   * and EventSource instance
   */
  setAuth: (token: string, id?: string) => void;

  /**
   * @description returns jwt auth token from response and moves it into setAuth method
   */
  initSession: () => Promise<string>;

  /**
   * @description change subscription for user
   * @todo describe response data
   */
  // subscriptionChange: (userId: string, newProductName: string, domain: string) => Promise<unknown>;

  /**
   * @description resets current user session, removes session id cookie
   */
  restoreSession: (id: string) => Promise<{ token: string; userServiceRefreshToken: string; userServiceToken: string }>;

  buyGuide: (productName: GuideType) => Promise<OrderPaymentsData[]>;

  getSubscriptionStatus: (email: string) => Promise<any>;

  getUserSubscriptionInfo: (
    subscriptionId: string,
    userId: string,
    userDomain: string,
  ) => Promise<IUserSubscriptionInfo>;

  getUserGuides: (userId: string) => Promise<IProduct[]>;

  /**
   * @description change password
   */
  changePassword: (email: string, newPassword: string, password: string) => Promise<void>;

  /**
   * @description cancel subscription user
   */
  // unsubscribeUser: (userId: string, domain: string) => Promise<void>;

  setProductNotification: (userId: string, guide: string) => Promise<void>;

  getUserInfo: () => Promise<IUser>;

  setUserInfo: (userData: IUserData, id: string) => Promise<IUserData>;

  setUserPhoto: (data: TUserPhoto, id: string) => Promise<TUserPhoto>;

  /**
   * @description set user style plan progress
   */
  setUserProgress: (userProgress: IUser, id: string) => Promise<any>;

  /**
   * @description get user style guide
   */
  getStyleGuide: (id: string) => Promise<IStyleGuide>;

  getGuide: (type: string, id: string, domain: string) => Promise<Blob>;

  /**
   * @description add bookmark for user
   */
  addBookmark: (userId: string, data: TBookmarkPayload) => Promise<void>;

  /**
   * @description remove bookmark for user
   */
  removeBookmark: (userId: string, data: TBookmarkPayload) => Promise<void>;
}

/**
 * @todo refactor into smaller files
 */
class Api implements IApi {
  private readonly substitutionDomain = SUBSTITUTION_DOMAIN;

  private readonly host = encodeURIComponent(this.substitutionDomain || 'account.lumi.place');

  public constructor(
    private readonly httpClient: IHttpClient = axios.create(),
    private readonly baseUrl = SERVER_URL,
  ) {}

  private eventSource: any;

  // public methods

  public setAuth: IApi['setAuth'] = (token: string, id?: string) => {
    const userId = useLocalStorageStore.getState().userId;
    this.setGlobalHeader('User-Id', userId || id);
    this.setGlobalHeader('Authorization', `Bearer ${token}`);
  };

  public initSession: IApi['initSession'] = async () => {
    // Use direct axios call without auth headers - this endpoint provides the token
    const response = await this.httpClient.get<{ token: string }>(`${this.baseUrl}/session?domain=${this.host}`);
    const { token } = response.data;
    if (token) this.setAuth(token);
    return token;
  };

  public restoreSession: IApi['restoreSession'] = async (id) => {
    // Use direct axios call without auth headers - this endpoint provides the token
    const response = await this.httpClient.get<{
      token: string;
      userServiceRefreshToken: string;
      userServiceToken: string;
    }>(`${this.baseUrl}/session/${id}?domain=${this.host}`);

    const { token, userServiceRefreshToken, userServiceToken } = response.data;
    if (token) this.setAuth(token);
    return { token, userServiceRefreshToken, userServiceToken };
  };

  public getUserGuides: IApi['getUserGuides'] = async (userId) => this.get(`/products/${userId}`);

  public getStyleGuide: IApi['getStyleGuide'] = async (userId) => this.get(`/styleguide/${userId}`);

  public getGuide: IApi['getGuide'] = async (type, id, domain) =>
    this.get(`/guide/${type}/${id}?domain=${domain}`, {
      responseType: 'blob',
      headers: { ContentType: 'application/pdf' },
    });

  public setProductNotification: IApi['setProductNotification'] = async (userId, guide) =>
    this.post(`/products/notification`, { userId, guide });

  public getUserInfo: IApi['getUserInfo'] = async () => this.get(`/me?app=${APP_TYPE}`);

  public setUserInfo: IApi['setUserInfo'] = async (userData, id) =>
    this.post(`/user/${id}`, { ...userData, app: APP_TYPE });

  public setUserPhoto: IApi['setUserPhoto'] = async (data, id) =>
    this.post(`/user/${id}/photo`, { ...data, app: APP_TYPE });

  public setUserProgress: IApi['setUserProgress'] = async (userData, id) =>
    this.post<any, any>(`/user/${id}`, { userData, app: APP_TYPE });

  // public subscriptionChange: IApi['subscriptionChange'] = async (userId, newProductName, domain) =>
  //   this.post<any, any>(`/subscription-change?domain=${domain}`, { userId, productId: newProductName });

  public buyGuide: IApi['buyGuide'] = async (productName) =>
    this.post('/additional-form', { productName, app: APP_TYPE });

  public getSubscriptionStatus: IApi['getSubscriptionStatus'] = async (email) => {
    const status = await this.get(`/subscription?domain=${this.host}&email=${email}`);
    return status;
  };

  public getUserSubscriptionInfo: IApi['getUserSubscriptionInfo'] = async (subscriptionId, userId, userDomain) =>
    await this.get(`/app/subscription/${subscriptionId}?userId=${userId}&domain=${userDomain}`);

  public getSubscriptionDiscounts = async (): Promise<Record<string, TSubscriptionDiscount>> => {
    return this.get('/products/discounts');
  };

  public signIn: IApi['signIn'] = async (payload) => {
    const { token, userId, userServiceRefreshToken, userServiceToken } = await this.post<
      Record<string, string>,
      { token: string; userId: string; userServiceRefreshToken: string; userServiceToken: string }
    >('/signin', { ...payload, app: APP_TYPE });

    if (token) this.setAuth(token, userId);
    return { token, userId, userServiceRefreshToken, userServiceToken };
  };

  public changePassword: IApi['changePassword'] = async (email, newPassword, password) =>
    this.post<any, any>('/change-password', { email, newPassword, password, app: APP_TYPE });

  // public unsubscribeUser: IApi['unsubscribeUser'] = async (userId, domain) =>
  //   this.post<any, any>(`/subscription-cancel/user?domain=${domain}`, { userId });

  public resetPassword: IApi['resetPassword'] = async (email) =>
    this.post<Record<string, string>, void>('/forgot', { email, app: APP_TYPE });

  // public renewSubscription: IApi['renewSubscription'] = async (userId) =>
  //   this.post<Record<string, string>, void>(`/subscription/renew`, { userId });

  public getUserSubscriptionsInfo = async (userId: string): Promise<TUserSubscriptionsInfo[]> =>
    this.get(`/app/subscriptions/${userId}`);

  public getMostLikedArticles = async (params: {
    count: number;
    excludeArticleIds?: string[];
  }): Promise<TGetLikeDislikesResponse> => {
    const userId = useLocalStorageStore.getState().userId;
    const query = new URLSearchParams({ insightsTake: String(params.count), domain: this.host });
    if (params.excludeArticleIds?.length) {
      query.set('insightsExcludeContentIds', params.excludeArticleIds.join(','));
    }
    return this.get(`/user/${userId}/like-dislike-articles?${query.toString()}`);
  };

  public getBookmarks = async (userId: string): Promise<TBookmarksResponse> => {
    return this.get(`/user/${userId}/bookmarks?app=${APP_TYPE}&domain=${this.host}`);
  };

  public addBookmark: IApi['addBookmark'] = async (userId, data) =>
    this.post(`/user/${userId}/bookmark`, { ...data, app: APP_TYPE });

  public removeBookmark: IApi['removeBookmark'] = async (userId, data) =>
    this.delete(`/user/${userId}/bookmark`, { ...data, app: APP_TYPE });

  public likeArticle = async (userId: string, data: TArticleLikePayload): Promise<void> =>
    this.post(`/user/${userId}/like-article`, data);

  public removeLikeArticle = async (userId: string, data: TArticleLikePayload): Promise<void> =>
    this.delete(`/user/${userId}/like-article`, data);

  public dislikeArticle = async (userId: string, data: TArticleLikePayload): Promise<void> =>
    this.post(`/user/${userId}/dislike-article`, data);

  public removeDislikeArticle = async (userId: string, data: TArticleLikePayload): Promise<void> =>
    this.delete(`/user/${userId}/dislike-article`, data);

  // private methods
  private getUrl = (url: string): string =>
    `${this.baseUrl}${url}${url.includes('domain=') ? '' : `?domain=${this.host}`}`;

  private async get<Response>(url: string, config?: any): Promise<Response> {
    const token = useLocalStorageStore.getState().token;
    const userId = useLocalStorageStore.getState().userId;
    const response = await this.httpClient.get<Response>(this.getUrl(url), {
      ...config,
      headers: {
        ...config?.headers,
        'User-Id': userId,
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  private async put<Data, Response>(url: string, data: Data): Promise<Response> {
    const token = useLocalStorageStore.getState().token;
    const userId = useLocalStorageStore.getState().userId;
    const response = await this.httpClient.put<Data, Response>(this.getUrl(url), data, {
      headers: {
        'User-Id': userId,
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  private async post<Data, Response>(url: string, data: Data): Promise<Response> {
    const token = useLocalStorageStore.getState().token;
    const userId = useLocalStorageStore.getState().userId;
    const response = await this.httpClient.post<Data, Response>(this.getUrl(url), data, {
      headers: {
        'User-Id': userId,
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  private async delete<Data, Response>(url: string, data: Data): Promise<Response> {
    const token = useLocalStorageStore.getState().token;
    const userId = useLocalStorageStore.getState().userId;
    const response = await this.httpClient.delete<Response>(this.getUrl(url), {
      data,
      headers: {
        'User-Id': userId,
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  private setGlobalHeader(headerName: string, value: string): void {
    (this.httpClient as AxiosInstance).defaults.headers.common[headerName] = value;
  }
}
export const api = new Api();
